import React from 'react';
import { X, ArrowLeft, ArrowRight, Save, Trash2, Plus, Image as ImageIcon, FileText, Film, Archive } from 'lucide-react';
import ValidatedInput from './ValidatedInput';
import ValidatedSelect from './ValidatedSelect';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/api.config';

const ProductFormModal = ({
  show,
  onClose,
  formData,
  setFormData,
  currentStep,
  setCurrentStep,
  onSubmit,
  categories,
  editing = false,
  formTouched,
  showAddCategory,
  setShowAddCategory,
  newCategoryName,
  setNewCategoryName,
  newCategoryDescription,
  setNewCategoryDescription,
  newCategoryIsOthers,
  setNewCategoryIsOthers,
  handleAddCategory
}) => {
  const paymentModes = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Credit', label: 'Credit' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Other', label: 'Other' }
  ];

  const gstOptions = [
    { value: '0', label: '0%' },
    { value: '5', label: '5%' },
    { value: '12', label: '12%' },
    { value: '18', label: '18%' },
    { value: '28', label: '28%' },
    { value: 'custom', label: 'Custom GST' }
  ];

  const dimensionUnits = [
    { value: 'cm', label: 'CM' },
    { value: 'inch', label: 'Inch' },
    { value: 'mm', label: 'MM' },
    { value: 'ft', label: 'Feet' }
  ];

  const estimationUnits = [
    { value: 'SFT', label: 'SFT' },
    { value: 'RFT', label: 'RFT' },
    { value: 'PCS', label: 'PCS' },
    { value: 'KG', label: 'KG' },
    { value: 'LTR', label: 'LTR' },
    { value: 'MTR', label: 'MTR' },
    { value: 'LUMPSUM', label: 'LUMPSUM' },
    { value: 'NOS', label: 'NOS' },
    { value: 'Others', label: 'Others' }
  ];

  const [showCustomUnit, setShowCustomUnit] = React.useState(false);
  const [customUnit, setCustomUnit] = React.useState('');

  React.useEffect(() => {
    if (formData.product.estimationUnit && !estimationUnits.find(u => u.value === formData.product.estimationUnit)) {
      setShowCustomUnit(true);
      setCustomUnit(formData.product.estimationUnit);
    } else if (formData.product.estimationUnit === 'Others') {
      setShowCustomUnit(true);
    } else {
      setShowCustomUnit(false);
    }
  }, [formData.product.estimationUnit]);

  if (!show) return null;


  // Validation logic
  const validateStep1 = () => {
    const { emailAddress, contactNumber } = formData.supplier;
    if (emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      return false;
    }
    if (contactNumber && contactNumber !== '0000000000' && !/^[0-9]{10}$/.test(contactNumber)) {
      return false;
    }
    return true;
  };

  const nextStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      } else {
        toast.error("Please fix the errors in the form before proceeding.");
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const prevStep = (e) => {
    if (e) e.preventDefault();
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleAddCategoryInternal = async () => {
    if (newCategoryName.trim()) {
      handleAddCategory(newCategoryName, newCategoryDescription, newCategoryIsOthers);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b flex-shrink-0 bg-slate-50">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-slate-900">
              {editing ? 'Edit Product' : 'Add New Product'}
            </h3>
            <div className="flex items-center mt-4 space-x-2 overflow-x-auto pb-1">
              {/* Step 1 Indicator */}
              <div className={`flex items-center space-x-2 whitespace-nowrap ${currentStep === 1 ? 'text-[#0A5F38]' : 'text-slate-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 1 ? 'bg-[#0A5F38] text-white shadow-md' : 'bg-slate-200 text-slate-500'}`}>1</div>
                <span className="text-sm font-semibold uppercase tracking-tight">Supplier</span>
              </div>
              <div className="w-6 h-px bg-slate-200"></div>

              {/* Step 2 Indicator */}
              <div className={`flex items-center space-x-2 whitespace-nowrap ${currentStep === 2 ? 'text-[#0A5F38]' : 'text-slate-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 2 ? 'bg-[#0A5F38] text-white shadow-md' : 'bg-slate-200 text-slate-500'}`}>2</div>
                <span className="text-sm font-semibold uppercase tracking-tight">Product</span>
              </div>
              <div className="w-6 h-px bg-slate-200"></div>

              {/* Step 3 Indicator */}
              <div className={`flex items-center space-x-2 whitespace-nowrap ${currentStep === 3 ? 'text-[#0A5F38]' : 'text-slate-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 3 ? 'bg-[#0A5F38] text-white shadow-md' : 'bg-slate-200 text-slate-500'}`}>3</div>
                <span className="text-sm font-semibold uppercase tracking-tight">Media & Catalogs</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden" noValidate>
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {currentStep === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="text-lg font-bold text-[#0A5F38]">Supplier Information</h4>
                  <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">Step 1 of 3</span>
                </div>

                <ValidatedInput
                  label="Supplier / Shop Name"
                  type="text"
                  value={formData.supplier.shopName}
                  onChange={(e) => setFormData({
                    ...formData,
                    supplier: { ...formData.supplier, shopName: e.target.value }
                  })}
                  validationType="optional"
                  placeholder="Enter supplier shop name"
                  showErrors={formTouched}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ValidatedInput
                    label="GST Number"
                    type="text"
                    value={formData.supplier.gstNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      supplier: { ...formData.supplier, gstNumber: e.target.value }
                    })}
                    placeholder="Enter GST number"
                    showErrors={formTouched}
                  />
                  <ValidatedInput
                    label="Invoice Number"
                    type="text"
                    value={formData.supplier.invoiceNumber}
                    onChange={(e) => setFormData({
                      ...formData,
                      supplier: { ...formData.supplier, invoiceNumber: e.target.value }
                    })}
                    placeholder="Enter invoice number"
                    showErrors={formTouched}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ValidatedInput
                    label="Contact Person"
                    type="text"
                    value={formData.supplier.contactPersonName}
                    onChange={(e) => setFormData({
                      ...formData,
                      supplier: { ...formData.supplier, contactPersonName: e.target.value }
                    })}
                    placeholder="Enter name"
                    showErrors={formTouched}
                  />
                  <ValidatedInput
                    label="Email Address"
                    type="email"
                    value={formData.supplier.emailAddress}
                    onChange={(e) => setFormData({
                      ...formData,
                      supplier: { ...formData.supplier, emailAddress: e.target.value }
                    })}
                    placeholder="Enter email"
                    showErrors={formTouched}
                  />
                </div>

                <ValidatedInput
                  label="Shop Address"
                  type="text"
                  value={formData.supplier.shopAddress}
                  onChange={(e) => setFormData({
                    ...formData,
                    supplier: { ...formData.supplier, shopAddress: e.target.value }
                  })}
                  placeholder="Enter full address"
                  showErrors={formTouched}
                />

                <ValidatedInput
                  label="Date of Purchase"
                  type="date"
                  value={formData.supplier.dateOfPurchase}
                  onChange={(e) => setFormData({
                    ...formData,
                    supplier: { ...formData.supplier, dateOfPurchase: e.target.value }
                  })}
                  showErrors={formTouched}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="text-lg font-bold text-[#0A5F38]">Product Information</h4>
                  <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">Step 2 of 3</span>
                </div>

                <div className="space-y-6">
                  {/* Variant Toggle Section */}
                  <div className="bg-[#0A5F38]/5 p-4 rounded-xl border border-[#0A5F38]/10 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-[#0A5F38] rounded-lg flex items-center justify-center text-white">
                          <Archive className="h-6 w-6" />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#0A5F38]">Configurable Product</h4>
                          <p className="text-xs text-[#0A5F38]/70">Enable this for products with different variations (e.g. Thickness, Size).</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.product.hasVariants}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            let newVariants = [...(formData.product.variants || [])];
                            
                            // If enabling and no variants exist, migrate current fields to first variant
                            if (enabled && newVariants.length === 0) {
                              newVariants = [{
                                attributeName: 'Thickness', // Default
                                attributeValue: '',
                                purchasePrice: formData.product.purchasePricePerUnit || 0,
                                sellingPrice: formData.product.sellingPrice || 0,
                                estimationCost: formData.product.estimationCost || 0,
                                estimationNotes: formData.product.estimationNotes || '',
                                installationFees: formData.product.installationFees || 0,
                                installationNotes: formData.product.installationNotes || '',
                                sku: formData.product.sku || '',
                                quantity: formData.product.quantity || 0
                              }];
                            }
                            
                            setFormData({
                              ...formData,
                              product: { 
                                ...formData.product, 
                                hasVariants: enabled,
                                variants: newVariants
                              }
                            });
                          }}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#0A5F38]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0A5F38]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Basic Info (Shared for both modes) */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-bold text-slate-700">Category</label>
                      <div className="flex gap-2">
                        <ValidatedSelect
                          value={formData.product.categoryId}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, categoryId: e.target.value }
                          })}
                          options={categories
                            .filter(cat => !cat.isOthers)
                            .map(cat => ({ value: cat.id, label: cat.name }))}
                          placeholder="Select category"
                          showErrors={formTouched}
                          className="flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAddCategory(true)}
                          className="px-4 py-2 bg-[#C5A57D] text-white text-sm font-bold rounded-lg hover:bg-[#B8985E] transition-shadow shadow-sm whitespace-nowrap"
                        >
                          + Add Category
                        </button>
                      </div>

                      {showAddCategory && (
                        <div className="bg-slate-50 p-5 rounded-xl border-2 border-[#C5A57D]/20 mt-3 space-y-4">
                          <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Create New Category</h4>
                          <input
                            type="text"
                            placeholder="What's the category name?"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent bg-white font-medium"
                          />
                          <div className="flex items-center space-x-2 p-2 bg-white rounded-lg border border-slate-200">
                            <input
                              type="checkbox"
                              id="newCategoryIsOthers"
                              checked={newCategoryIsOthers}
                              onChange={(e) => setNewCategoryIsOthers(e.target.checked)}
                              className="h-4 w-4 text-[#0A5F38] rounded border-slate-300"
                            />
                            <label htmlFor="newCategoryIsOthers" className="text-sm font-medium text-slate-700">
                              Custom entry for this category
                            </label>
                          </div>
                          <div className="flex gap-3 pt-2">
                            <button
                              type="button"
                              onClick={handleAddCategoryInternal}
                              disabled={!newCategoryName.trim()}
                              className="flex-1 py-2 bg-[#0A5F38] text-white text-sm font-bold rounded-lg hover:brightness-110 disabled:grayscale transition-all"
                            >
                              Save Category
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowAddCategory(false)}
                              className="flex-1 py-2 bg-slate-200 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <ValidatedInput
                      label="Product Name"
                      value={formData.product.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        product: { ...formData.product, name: e.target.value }
                      })}
                      placeholder="Enter product name"
                      showErrors={formTouched}
                      disabled={!formData.product.categoryId}
                    />

                    <ValidatedInput
                      label="Product Description"
                      value={formData.product.description}
                      onChange={(e) => setFormData({
                        ...formData,
                        product: { ...formData.product, description: e.target.value }
                      })}
                      placeholder="Brief details about the product"
                      showErrors={formTouched}
                    />
                  </div>

                  {!formData.product.hasVariants ? (
                    <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-500">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ValidatedInput
                          label="Estimation Cost ₹"
                          tooltip="This cost is used by the AI chatbot to provide estimated pricing to customers when they inquire about this product."
                          type="number"
                          value={formData.product.estimationCost}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, estimationCost: e.target.value }
                          })}
                          placeholder="Estimated price for chatbot"
                          showErrors={formTouched}
                        />
                        <div className="space-y-2">
                          <ValidatedSelect
                            label="Unit"
                            tooltip="The measurement unit for the estimation cost (e.g., Price per Square Foot/SFT, Piece/PCS)."
                            value={showCustomUnit ? 'Others' : formData.product.estimationUnit}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'Others') {
                                setShowCustomUnit(true);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: customUnit || 'Others' }
                                });
                              } else {
                                setShowCustomUnit(false);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: val }
                                });
                              }
                            }}
                            options={estimationUnits}
                            showErrors={formTouched}
                          />
                          {showCustomUnit && (
                            <input
                              type="text"
                              placeholder="Enter custom unit (e.g. bundle, set)"
                              value={customUnit}
                              onChange={(e) => {
                                const val = e.target.value;
                                setCustomUnit(val);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: val }
                                });
                              }}
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white text-sm font-medium animate-in fade-in slide-in-from-top-1"
                            />
                          )}
                        </div>
                      </div>

                      <ValidatedInput
                        label="Estimation Note"
                        tooltip="Additional note about the estimation price (e.g. Prices may vary on bulk orders)."
                        type="text"
                        value={formData.product.estimationNotes || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          product: { ...formData.product, estimationNotes: e.target.value }
                        })}
                        placeholder="Enter estimation note"
                        showErrors={formTouched}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ValidatedInput
                          label="Installation Fees ₹"
                          tooltip="Additional fees applied for product installation services."
                          type="number"
                          value={formData.product.installationFees}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, installationFees: e.target.value }
                          })}
                          placeholder="Enter installation fees"
                          showErrors={formTouched}
                        />
                        <ValidatedInput
                          label="Installation Note"
                          tooltip="Additional note about installation fees (e.g. Installation fee may reduce on bulk orders)."
                          type="text"
                          value={formData.product.installationNotes || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, installationNotes: e.target.value }
                          })}
                          placeholder="Enter installation note"
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ValidatedInput
                          label="Purchase ₹"
                          type="number"
                          value={formData.product.purchasePricePerUnit}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, purchasePricePerUnit: e.target.value }
                          })}
                          showErrors={formTouched}
                        />
                        <ValidatedInput
                          label="Selling ₹"
                          type="number"
                          value={formData.product.sellingPrice}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, sellingPrice: e.target.value }
                          })}
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ValidatedInput
                          label="HSN Code"
                          value={formData.product.hsnCode}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, hsnCode: e.target.value }
                          })}
                          placeholder="Tax category code"
                          showErrors={formTouched}
                        />
                        <ValidatedSelect
                          label="GST %"
                          value={formData.product.gstPercentage}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, gstPercentage: e.target.value }
                          })}
                          options={gstOptions}
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <input
                          type="checkbox"
                          id="hasWarranty"
                          checked={formData.product?.warranty?.hasWarranty || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: {
                              ...formData.product,
                              warranty: { ...(formData.product?.warranty || {}), hasWarranty: e.target.checked }
                            }
                          })}
                          className="h-5 w-5 text-[#0A5F38] rounded border-slate-300"
                        />
                        <label htmlFor="hasWarranty" className="text-sm font-bold text-slate-700">Include Warranty</label>

                        {formData.product?.warranty?.hasWarranty && (
                          <div className="flex-1 flex gap-3 animate-in fade-in slide-in-from-left-2 transition-all">
                            <input
                              type="number"
                              placeholder="Months"
                              value={formData.product?.warranty?.warrantyPeriod || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                product: {
                                  ...formData.product,
                                  warranty: { ...(formData.product?.warranty || {}), warrantyPeriod: e.target.value }
                                }
                              })}
                              className="w-24 px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                            <input
                              type="text"
                              placeholder="Warranty coverage details..."
                              value={formData.product?.warranty?.warrantyDetails || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                product: {
                                  ...formData.product,
                                  warranty: { ...(formData.product?.warranty || {}), warrantyDetails: e.target.value }
                                }
                              })}
                              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ValidatedInput
                          label="Variant Attribute Type"
                          tooltip="The property that varies (e.g., Thickness, Size, Material)."
                          value={formData.product.variants?.[0]?.attributeName || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            const updated = formData.product.variants.map(v => ({ ...v, attributeName: val }));
                            setFormData({
                              ...formData,
                              product: { ...formData.product, variants: updated }
                            });
                          }}
                          placeholder="e.g. Thickness"
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="border rounded-xl overflow-hidden shadow-sm bg-white">
                        <div className="bg-slate-50 px-4 py-2 border-b flex justify-between items-center">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Product Variants</span>
                          <button
                            type="button"
                            onClick={() => {
                              const lastVariant = formData.product.variants?.[formData.product.variants.length - 1] || {};
                              const newVariant = {
                                attributeName: lastVariant.attributeName || 'Thickness',
                                attributeValue: '',
                                purchasePrice: lastVariant.purchasePrice || 0,
                                sellingPrice: lastVariant.sellingPrice || 0,
                                estimationCost: lastVariant.estimationCost || 0,
                                estimationNotes: lastVariant.estimationNotes || '',
                                installationFees: lastVariant.installationFees || 0,
                                installationNotes: lastVariant.installationNotes || '',
                                sku: '',
                                quantity: 0
                              };
                              setFormData({
                                ...formData,
                                product: { 
                                  ...formData.product, 
                                  variants: [...(formData.product.variants || []), newVariant] 
                                }
                              });
                            }}
                            className="text-[#0A5F38] hover:bg-[#0A5F38]/10 p-1.5 rounded-lg transition-colors flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span className="text-xs font-bold uppercase">Add Variant</span>
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-slate-50/50">
                              <tr>
                                <th className="px-4 py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">Variant Value</th>
                                <th className="px-4 py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider w-32">Purchase Price</th>
                                <th className="px-4 py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider w-32">Selling Price</th>
                                <th className="px-4 py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider w-32">Installation</th>
                                <th className="px-4 py-2 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider w-32">Estimation</th>
                                <th className="px-4 py-2 text-right"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {formData.product.variants?.map((variant, index) => (
                                <tr key={index} className="hover:bg-slate-50/30 transition-colors">
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      className="w-full text-sm border-0 focus:ring-0 p-1 bg-transparent text-center placeholder:text-slate-300"
                                      value={variant.attributeValue}
                                      onChange={(e) => {
                                        const updated = [...formData.product.variants];
                                        updated[index].attributeValue = e.target.value;
                                        setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                      }}
                                      placeholder="Value"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      className="w-full text-sm border-0 focus:ring-0 p-1 bg-transparent text-center placeholder:text-slate-300"
                                      value={variant.purchasePrice}
                                      onChange={(e) => {
                                        const updated = [...formData.product.variants];
                                        updated[index].purchasePrice = e.target.value;
                                        setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                      }}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      className="w-full text-sm border-0 focus:ring-0 p-1 bg-transparent text-center font-semibold text-[#0A5F38] placeholder:text-slate-300"
                                      value={variant.sellingPrice}
                                      onChange={(e) => {
                                        const updated = [...formData.product.variants];
                                        updated[index].sellingPrice = e.target.value;
                                        setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                      }}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      className="w-full text-sm border-0 focus:ring-0 p-1 bg-transparent text-center placeholder:text-slate-300"
                                      value={variant.installationFees}
                                      onChange={(e) => {
                                        const updated = [...formData.product.variants];
                                        updated[index].installationFees = e.target.value;
                                        setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                      }}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      className="w-full text-sm border-0 focus:ring-0 p-1 bg-transparent text-center placeholder:text-slate-300"
                                      value={variant.estimationCost}
                                      onChange={(e) => {
                                        const updated = [...formData.product.variants];
                                        updated[index].estimationCost = e.target.value;
                                        setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                      }}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {formData.product.variants.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updated = formData.product.variants.filter((_, i) => i !== index);
                                          setFormData({ ...formData, product: { ...formData.product, variants: updated } });
                                        }}
                                        className="text-red-400 hover:text-red-600 transition-colors p-1"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <ValidatedSelect
                            label="Estimation Unit"
                            tooltip="The measurement unit for the estimation cost (e.g., Price per Square Foot/SFT, Piece/PCS)."
                            value={showCustomUnit ? 'Others' : formData.product.estimationUnit}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'Others') {
                                setShowCustomUnit(true);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: customUnit || 'Others' }
                                });
                              } else {
                                setShowCustomUnit(false);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: val }
                                });
                              }
                            }}
                            options={estimationUnits}
                            showErrors={formTouched}
                          />
                          {showCustomUnit && (
                            <input
                              type="text"
                              placeholder="Enter custom unit (e.g. bundle, set)"
                              value={customUnit}
                              onChange={(e) => {
                                const val = e.target.value;
                                setCustomUnit(val);
                                setFormData({
                                  ...formData,
                                  product: { ...formData.product, estimationUnit: val }
                                });
                              }}
                              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white text-sm font-medium animate-in fade-in slide-in-from-top-1"
                            />
                          )}
                        </div>
                        <ValidatedInput
                          label="Notes (Optional)"
                          tooltip="Shared note for all variants regarding pricing/installation."
                          type="text"
                          value={formData.product.estimationNotes || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, estimationNotes: e.target.value }
                          })}
                          placeholder="e.g. Prices may vary on bulk orders"
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ValidatedInput
                          label="HSN Code"
                          value={formData.product.hsnCode}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, hsnCode: e.target.value }
                          })}
                          placeholder="Tax category code"
                          showErrors={formTouched}
                        />
                        <ValidatedSelect
                          label="GST %"
                          value={formData.product.gstPercentage}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: { ...formData.product, gstPercentage: e.target.value }
                          })}
                          options={gstOptions}
                          showErrors={formTouched}
                        />
                      </div>

                      <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <input
                          type="checkbox"
                          id="hasWarranty"
                          checked={formData.product?.warranty?.hasWarranty || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            product: {
                              ...formData.product,
                              warranty: { ...(formData.product?.warranty || {}), hasWarranty: e.target.checked }
                            }
                          })}
                          className="h-5 w-5 text-[#0A5F38] rounded border-slate-300"
                        />
                        <label htmlFor="hasWarranty" className="text-sm font-bold text-slate-700">Include Warranty</label>

                        {formData.product?.warranty?.hasWarranty && (
                          <div className="flex-1 flex gap-3 animate-in fade-in slide-in-from-left-2 transition-all">
                            <input
                              type="number"
                              placeholder="Months"
                              value={formData.product?.warranty?.warrantyPeriod || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                product: {
                                  ...formData.product,
                                  warranty: { ...(formData.product?.warranty || {}), warrantyPeriod: e.target.value }
                                }
                              })}
                              className="w-24 px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                            <input
                              type="text"
                              placeholder="Warranty coverage details..."
                              value={formData.product?.warranty?.warrantyDetails || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                product: {
                                  ...formData.product,
                                  warranty: { ...(formData.product?.warranty || {}), warrantyDetails: e.target.value }
                                }
                              })}
                              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="text-lg font-bold text-[#0A5F38]">Media & Catalogs</h4>
                  <span className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">Step 3 of 3</span>
                </div>

                <div className="flex flex-col space-y-4">
                  <div className="p-10 border-2 border-dashed border-slate-300 rounded-2xl bg-slate-50 hover:bg-slate-100/50 hover:border-[#C5A57D]/50 transition-all cursor-pointer relative group text-center">
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/*,application/pdf,application/zip,application/x-zip-compressed"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setFormData({
                          ...formData,
                          product: {
                            ...formData.product,
                            catalogs: [...(formData.product.catalogs || []), ...files]
                          }
                        });
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className="flex flex-col items-center">
                      <div className="p-4 bg-white rounded-2xl shadow-sm mb-4 group-hover:scale-110 transition-transform">
                        <Plus className="h-8 w-8 text-[#C5A57D]" />
                      </div>
                      <h5 className="text-base font-bold text-slate-800">Add Project Content</h5>
                      <p className="text-sm text-slate-500 mt-2 max-w-sm">
                        Upload high-quality images, PDFs, ZIP files, or MP4 videos to showcase in the AI chatbot.
                      </p>
                    </div>
                  </div>

                  {formData.product.catalogs && formData.product.catalogs.length > 0 && (
                    <div className="pt-4 animate-in fade-in duration-500">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center">
                          <span className="w-1.5 h-1.5 bg-[#C5A57D] rounded-full mr-2"></span>
                          Uploaded Files ({formData.product.catalogs.length})
                        </h5>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {formData.product.catalogs.map((file, idx) => {
                          const isFile = file instanceof File;
                          const isImage = isFile ? file.type?.startsWith('image/') : file.fileType === 'image';
                          const isVideo = isFile ? file.type?.startsWith('video/') : file.fileType === 'video';
                          const isZip = isFile ? (file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || file.name.toLowerCase().endsWith('.zip')) : file.fileType === 'zip';
                          const previewUrl = isFile ? URL.createObjectURL(file) : (file.url ? `${API_BASE_URL}${file.url}` : null);

                          return (
                            <div key={idx} className="group flex items-center p-3 bg-white rounded-xl border border-slate-200 hover:border-[#C5A57D] hover:shadow-md transition-all overflow-hidden relative">
                              <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-slate-50 rounded-lg border border-slate-100 overflow-hidden group-hover:bg-[#C5A57D]/5 transition-colors">
                                {isImage && previewUrl ? (
                                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                                ) : isVideo ? (
                                  <Film className="h-6 w-6 text-slate-400" />
                                ) : isZip ? (
                                  <Archive className="h-6 w-6 text-slate-400" />
                                ) : (
                                  <FileText className="h-6 w-6 text-slate-400" />
                                )}
                              </div>
                              <div className="flex flex-col flex-1 min-w-0 ml-4">
                                <span className="text-sm font-bold text-slate-900 truncate pr-8">
                                  {file.name || file.originalName || 'File'}
                                </span>
                                <div className="flex items-center space-x-2 mt-0.5">
                                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter bg-slate-100 px-1.5 py-0.5 rounded">
                                    {isFile ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : (file.fileType || 'Asset')}
                                  </span>
                                  {!isFile && <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-tighter bg-emerald-50 px-1.5 py-0.5 rounded">Uploaded</span>}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newCatalogs = formData.product.catalogs.filter((_, i) => i !== idx);
                                  setFormData({
                                    ...formData,
                                    product: { ...formData.product, catalogs: newCatalogs }
                                  });
                                }}
                                className="absolute right-2 top-2 p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-all opacity-0 group-hover:opacity-100 transform translate-x-1 group-hover:translate-x-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-slate-50 flex items-center justify-between flex-shrink-0">
            <div className="flex space-x-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-100 transition-all flex items-center space-x-2 shadow-sm"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-slate-500 font-bold hover:text-slate-800 transition-colors"
              >
                Exit
              </button>
            </div>

            <div className="flex">
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-2.5 bg-[#0A5F38] text-white font-bold rounded-xl hover:brightness-110 shadow-lg shadow-emerald-900/10 transition-all flex items-center space-x-2"
                >
                  <span>Continue</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="px-10 py-2.5 bg-[#0A5F38] text-white font-bold rounded-xl hover:brightness-110 shadow-lg shadow-emerald-900/10 transition-all flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>{editing ? 'Update changes' : 'Create Product'}</span>
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
